import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CreateCharacterRequest, PlayerAppearance, VALID_RACES, VALID_CLASSES } from '../types/character';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import PlayerAppearanceEditor from './PlayerAppearanceEditor';
import './CharacterCreator.css';

interface CharacterCreatorProps {
  onCharacterCreated: () => void;
  onCancel: () => void;
}

const CharacterCreator: React.FC<CharacterCreatorProps> = ({ onCharacterCreated, onCancel }) => {
  const { handleAuthError } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState<CreateCharacterRequest>({
    name: '',
    race: 'Human',
    class: 'Warrior',
    gender: 'Male',
    skinTone: 'default',
    hairSprite: null,
    beardSprite: null,
    vitality: 10,
    intelligence: 10,
    strength: 10,
    dexterity: 10,
    constitution: 10,
    wisdom: 10,
  });

  const appearance: PlayerAppearance = {
    gender: formData.gender,
    skinTone: formData.skinTone,
    hairSprite: formData.hairSprite ?? null,
    beardSprite: formData.beardSprite ?? null,
  };

  const handleAppearanceChange = (next: PlayerAppearance) => {
    setFormData(prev => ({
      ...prev,
      gender: next.gender,
      skinTone: next.skinTone,
      hairSprite: next.hairSprite,
      beardSprite: next.beardSprite,
    }));
  };

  const TOTAL_STAT_POINTS = 60;
  const MIN_STAT = 8;
  const MAX_STAT = 18;

  const totalAllocatedStats = formData.vitality + formData.intelligence + formData.strength + 
                            formData.dexterity + formData.constitution + formData.wisdom;
  const remainingPoints = TOTAL_STAT_POINTS - totalAllocatedStats;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'name' || name === 'race' || name === 'class' ? value : parseInt(value)
    }));
  };

  const handleStatChange = (stat: keyof CreateCharacterRequest, delta: number) => {
    const currentValue = formData[stat] as number;
    const newValue = currentValue + delta;
    
    if (newValue >= MIN_STAT && newValue <= MAX_STAT) {
      if (delta > 0 && remainingPoints > 0) {
        setFormData(prev => ({ ...prev, [stat]: newValue }));
      } else if (delta < 0) {
        setFormData(prev => ({ ...prev, [stat]: newValue }));
      }
    }
  };

  const resetStats = () => {
    setFormData(prev => ({
      ...prev,
      vitality: 10,
      intelligence: 10,
      strength: 10,
      dexterity: 10,
      constitution: 10,
      wisdom: 10,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!formData.name.trim()) {
      setError('Character name is required');
      setIsLoading(false);
      return;
    }

    if (remainingPoints !== 0) {
      setError('You must allocate all stat points');
      setIsLoading(false);
      return;
    }

    try {
      const response = await makeAuthenticatedRequest('/characters', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to create character');
      }

      onCharacterCreated();
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="character-creator">
      <h2>Create New Character</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Character Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            maxLength={50}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="race">Race:</label>
            <select
              id="race"
              name="race"
              value={formData.race}
              onChange={handleInputChange}
            >
              {VALID_RACES.map(race => (
                <option key={race} value={race}>{race}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="class">Class:</label>
            <select
              id="class"
              name="class"
              value={formData.class}
              onChange={handleInputChange}
            >
              {VALID_CLASSES.map(characterClass => (
                <option key={characterClass} value={characterClass}>{characterClass}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Appearance:</label>
          <PlayerAppearanceEditor
            race={formData.race}
            value={appearance}
            onChange={handleAppearanceChange}
          />
        </div>

        <div className="stats-section">
          <div className="stats-header">
            <h3>Stat Allocation</h3>
            <div className="stat-points">
              Points Remaining: <span className={remainingPoints === 0 ? 'points-zero' : 'points-available'}>{remainingPoints}</span>
            </div>
            <button type="button" onClick={resetStats} className="reset-button">Reset Stats</button>
          </div>

          <div className="stats-grid">
            {[
              { key: 'vitality', label: 'Vitality', description: 'Increases HP' },
              { key: 'strength', label: 'Strength', description: 'Increases damage' },
              { key: 'constitution', label: 'Constitution', description: 'Increases HP' },
            ].map(({ key, label, description }) => (
              <div key={key} className="stat-row">
                <div className="stat-info">
                  <label>{label}:</label>
                  <small>{description}</small>
                </div>
                <div className="stat-controls">
                  <button
                    type="button"
                    onClick={() => handleStatChange(key as keyof CreateCharacterRequest, -1)}
                    disabled={(formData[key as keyof CreateCharacterRequest] as number) <= MIN_STAT}
                  >
                    -
                  </button>
                  <span className="stat-value">{formData[key as keyof CreateCharacterRequest]}</span>
                  <button
                    type="button"
                    onClick={() => handleStatChange(key as keyof CreateCharacterRequest, 1)}
                    disabled={(formData[key as keyof CreateCharacterRequest] as number) >= MAX_STAT || remainingPoints <= 0}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="form-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button 
            type="submit" 
            disabled={isLoading || remainingPoints !== 0}
          >
            {isLoading ? 'Creating...' : 'Create Character'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CharacterCreator;